import { useEffect, useRef, useState } from "react";
import { useAuth } from "../lib/auth";
import { ShieldCheck, Edit3, ChevronDown, ChevronUp, X, PlusCircle } from "lucide-react";

const LS_KEY = "atom.examiner.annotations";

/**
 * Examiner mode banner — visible across the app when toggled on.
 * Adds a top strip, blocks all mutating buttons, and offers a slide-in annotations sidebar.
 */
export function ExaminerBanner() {
  const { examinerMode, toggleExaminer, user } = useAuth();
  const [open, setOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [notes, setNotes] = useState([]);
  const [draft, setDraft] = useState("");

  useEffect(() => {
    if (!examinerMode) return;
    setNotes(JSON.parse(localStorage.getItem(LS_KEY) || "[]"));
    // Disable mutating buttons across the app
    document.body.classList.add("examiner-mode");
    return () => document.body.classList.remove("examiner-mode");
  }, [examinerMode]);

  const saveNote = () => {
    if (!draft.trim()) return;
    const n = {
      id: `EX-${Date.now()}`,
      text: draft.trim(),
      ts: new Date().toISOString(),
      page: window.location.pathname,
      examiner: user?.name || "External examiner",
    };
    const next = [n, ...notes];
    setNotes(next);
    localStorage.setItem(LS_KEY, JSON.stringify(next));
    setDraft("");
  };

  const deleteNote = (id) => {
    const next = notes.filter(n => n.id !== id);
    setNotes(next);
    localStorage.setItem(LS_KEY, JSON.stringify(next));
  };

  const exportNotes = () => {
    const blob = new Blob([JSON.stringify({ examiner: user?.name, exported_at: new Date().toISOString(), notes }, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `examiner-annotations-${Date.now()}.json`;
    a.click();
  };

  if (!examinerMode) return null;

  return (
    <>
      {/* Top banner */}
      <div className="fixed top-0 inset-x-0 z-50 bg-amber/95 text-ink border-b-2 border-amber-700 backdrop-blur-sm flex items-center gap-3 px-4 py-1.5 text-[11px] font-medium" data-testid="examiner-banner">
        <span className="inline-flex items-center gap-1.5">
          <ShieldCheck size={12} strokeWidth={1.5} />
          EXAMINER MODE · READ-ONLY
        </span>
        <span className="text-ink-muted">All mutating actions are disabled. Annotations are recorded locally and exportable.</span>
        <div className="ml-auto flex items-center gap-2">
          <button data-testid="examiner-notes-toggle" onClick={() => setOpen(o => !o)} className="px-2 py-0.5 bg-ink text-white rounded-full inline-flex items-center gap-1 text-[10px] hover:opacity-90">
            <Edit3 size={10} strokeWidth={1.5} /> {notes.length} annotation{notes.length === 1 ? "" : "s"}
          </button>
          <button onClick={toggleExaminer} className="px-2 py-0.5 border border-ink/30 rounded-full text-[10px] hover:bg-ink/10">Exit examiner mode</button>
        </div>
      </div>
      <div className="h-7" />

      {/* Annotation sidebar */}
      {open && (
        <div data-testid="examiner-sidebar" className="fixed right-0 top-8 bottom-0 w-[360px] bg-white border-l border-line shadow-[0_0_24px_rgba(11,43,92,0.12)] z-40 flex flex-col">
          <div className="px-4 py-3 border-b border-line flex items-center justify-between bg-amber/10">
            <div>
              <div className="text-[13px] font-medium tracking-tight">Examiner annotations</div>
              <div className="text-[10px] text-ink-subtle">{user?.name || "External examiner"} · {notes.length} note{notes.length === 1 ? "" : "s"}</div>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => setCollapsed(c => !c)} className="p-1 hover:bg-bg-hover rounded">{collapsed ? <ChevronDown size={13} strokeWidth={1.5} /> : <ChevronUp size={13} strokeWidth={1.5} />}</button>
              <button onClick={() => setOpen(false)} className="p-1 hover:bg-bg-hover rounded"><X size={13} strokeWidth={1.5} /></button>
            </div>
          </div>

          {!collapsed && (
            <>
              {/* Add note */}
              <div className="p-3 border-b border-line">
                <textarea
                  data-testid="examiner-note-input"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  placeholder={`Add a note about this page (${window.location.pathname})…`}
                  rows={3}
                  className="w-full px-3 py-2 text-[12px] bg-bg-warm border border-line rounded-lg focus:outline-none focus:ring-2 focus:ring-amber/30 focus:border-amber resize-none"
                />
                <button data-testid="examiner-note-save" onClick={saveNote} disabled={!draft.trim()} className="w-full mt-2 px-3 py-2 text-[12px] bg-ink text-white rounded-md hover:bg-navy disabled:opacity-50 inline-flex items-center justify-center gap-1.5">
                  <PlusCircle size={12} strokeWidth={1.5} /> Save annotation
                </button>
              </div>

              {/* Notes list */}
              <div className="flex-1 overflow-y-auto p-3 space-y-2">
                {notes.length === 0 ? (
                  <div className="text-[12px] text-ink-subtle text-center py-8">No annotations yet. Add one above.</div>
                ) : notes.map(n => (
                  <div key={n.id} className="border border-line rounded-lg p-2.5 hover:bg-bg-warm">
                    <div className="flex items-start justify-between mb-1.5">
                      <span className="text-[10px] hash-mono text-ink-subtle">{n.id}</span>
                      <button onClick={() => deleteNote(n.id)} className="text-ink-subtle hover:text-risk-veryhigh"><X size={11} strokeWidth={1.5} /></button>
                    </div>
                    <div className="text-[12px] text-ink whitespace-pre-wrap">{n.text}</div>
                    <div className="text-[10px] text-ink-subtle mt-2 flex justify-between items-center">
                      <span>{n.page}</span>
                      <span>{new Date(n.ts).toLocaleString("en-GB", {dateStyle: "short", timeStyle: "short"})}</span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="p-3 border-t border-line flex gap-2">
                <button onClick={exportNotes} disabled={notes.length === 0} className="flex-1 px-3 py-1.5 text-[11px] border border-line rounded-md hover:bg-bg-hover disabled:opacity-50">Export JSON</button>
                <button onClick={() => { if (window.confirm("Clear all annotations?")) { setNotes([]); localStorage.removeItem(LS_KEY); } }} className="px-3 py-1.5 text-[11px] border border-line rounded-md hover:bg-bg-hover">Clear</button>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}
